/**
 * Enquiry Checklist (ORC) section labels — NTES-QSF-SAL-02.
 * Keep in sync with backend/src/modules/crm/order-review-checklist-sections.ts
 */

export const ORC_SECTION_KEYS = ['enquiry_product_information', 'review_of_enquiry'] as const;

export type OrderReviewChecklistSectionKey = (typeof ORC_SECTION_KEYS)[number];

export const ORC_PRODUCT_INFO_ITEM_LABELS = [
  'Description of Enquiry & Quantity. Note: In case of multiple products or line items (more than 1 or 2 types), attach the list of products with technical information along with the contract review sheet.',
  'Any Additional Comments?',
] as const;

export const ORC_REVIEW_OF_ENQUIRY_ITEM_LABELS = [
  'Is the enquired product within our scope of products/services?',
  'Are the terms and conditions of the enquiry reviewed & acceptable?',
  'Are there any delivery or post-delivery activities applicable? If applicable, are they acceptable?',
  'If applicable, are the delivery and post-delivery terms acceptable?',
  'Does the company have required manpower/personnel to execute the job?',
  'Are requirements not stated by the customer but necessary for provision of the product reviewed (capability, materials, equipment, work environment, inspection/test, etc.) and acceptable?',
  'Are any processes outsourced related to this enquiry? If yes, identify the outsourced process (in remarks).',
  'Are any legal (statutory/regulatory) and other requirements identified and acceptable?',
  'Are required/applicable documents (drawings/specifications/WI, etc.) available?',
  'Are there any amendments in the received enquiry? If yes, are they notified to relevant personnel?',
  'Is this enquiry reviewed / agreed to send the offer?',
] as const;

export const ORC_REVIEW_OFFER_ITEM_LABEL = ORC_REVIEW_OF_ENQUIRY_ITEM_LABELS[10];

export const ORC_SECTION_ITEM_LABELS: Record<OrderReviewChecklistSectionKey, readonly string[]> = {
  enquiry_product_information: ORC_PRODUCT_INFO_ITEM_LABELS,
  review_of_enquiry: ORC_REVIEW_OF_ENQUIRY_ITEM_LABELS,
};

export const ORC_SECTION_TITLES: Record<string, string> = {
  enquiry_product_information: 'Description of Enquiry / Product Information',
  review_of_enquiry: 'Review of Enquiry',
  // Legacy keys (existing ORCs)
  communication_inquiry_review: 'Communication Inquiry Review',
  technical_review: 'Technical Review',
  execution_manufacturing_review: 'Execution Manufacturing Review',
  commercial_review: 'Commercial Review',
  supply_chain_review: 'Supply Chain Review',
  quality_compliance_review: 'Quality Compliance Review',
  risk_assessment: 'Risk Assessment',
  final_recommendation: 'Final Recommendation',
};

export function formatOrcSectionTitle(key: string): string {
  return ORC_SECTION_TITLES[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getDefaultOrcItemLabel(sectionKey: string, index: number): string {
  const labels = ORC_SECTION_ITEM_LABELS[sectionKey as OrderReviewChecklistSectionKey];
  return labels?.[index] || '';
}

export function isProductInfoSection(sectionKey: string): boolean {
  return sectionKey === 'enquiry_product_information';
}

export function isReviewOfferItem(sectionKey: string, label: string): boolean {
  return (
    sectionKey === 'review_of_enquiry' &&
    String(label || '').trim() === ORC_REVIEW_OFFER_ITEM_LABEL
  );
}

export function getReviewOfferFromSections(
  sections: Record<string, Array<{ label?: string; response?: string; remarks?: string }>> | undefined
): { response: string; remarks: string } | null {
  const list = sections?.review_of_enquiry;
  if (!Array.isArray(list)) return null;
  const item =
    list.find((i) => String(i.label || '').trim() === ORC_REVIEW_OFFER_ITEM_LABEL) ||
    list[list.length - 1];
  if (!item) return null;
  return { response: String(item.response || ''), remarks: String(item.remarks || '') };
}
